import Vue from 'vue';
import { computed, ref, Ref } from '@vue/composition-api';
import { getUrlParam } from '@tencent/magic-ui-util';
import { Page } from '../../types';

const useQrcode = (pageConfig: Ref<Page>) => {
  const { inPrerender, env } = Vue.prototype;
  const showQrcode = computed(() => {
    // 在预渲染和魔方环境中不显示二维码
    if (inPrerender) return false;
    return env.isWeb && !getUrlParam('__no_magic_qrcode');
  });

  // 如果 showQrcode 需要手动隐藏 loading
  if (showQrcode.value) {
    document.getElementById('m_page_loading_div')?.remove();
  }

  const isPad = computed(() => env.isIpad || env.isApad || getUrlParam('_device').includes('-h'));

  // 判断是否折叠屏
  const getIsFoldScreen = () => {
    const { clientWidth, clientHeight } = document.documentElement;
    const rate = clientWidth / clientHeight;
    // 折叠屏宽高比区间
    return 0.75 <= rate && rate <= 1.3;
  };

  const isFoldScreen = ref(getIsFoldScreen());

  window.addEventListener('resize', () => {
    isFoldScreen.value = getIsFoldScreen();
  });

  return {
    showQrcode,
    qrcodeConfig: ref({
      url: location.href,
    }),

    /**
     * 是否出现【屏幕方向不符合预期】的提示
     *
     * 搭配 customOrientation 来使用
     *
     * 取值范围:
     * -（如果屏幕方向不符合预期时）出现 (true)
     * -（如果屏幕方向不符合预期时）不出现 (false)
     */
    isShowOrientationTip: computed(() => {
      // 在预渲染和魔方环境中（不管符不符合）都不出现此提示
      if (inPrerender) {
        return false;
      }

      // 如果管理台设置了屏幕方向提示，则使用管理台的配置
      if (typeof pageConfig.value?.screenOrientation !== 'undefined') {
        return pageConfig.value?.screenOrientation;
      }

      // 默认情况下，（如果屏幕方向不符合预期）会出现此提示
      return true;
    }),

    /**
     * 屏幕的预期方向
     *
     * 在【屏幕方向提示组件(orientation-tip)】中，
     * 当【屏幕的预期展示方向】与【实际屏幕方向】不一致时，会判断是否需要出现提示 (isShowOrientationTip)，
     * 如果需要提示，则会出现提示
     * 如果不需要提示，则不会出现提示
     *
     * 当【屏幕的预期展示方向】与【实际屏幕方向】一致时，则不会出现提示
     *
     * 取值范围:
     * - 横屏 (landscape)
     * - 竖屏 (portrait)
     * - 都可以 (all)
     *
     */
    customOrientation: computed(() => {
      // 管理台配置了横屏方向
      if (pageConfig.value?.isLandscape) {
        return 'landscape';
      }

      // pad 横竖屏方向都支持
      if (isPad.value || isFoldScreen.value) {
        return 'all';
      }

      // 默认竖屏方向
      return 'portrait';
    }),
  };
};

export default useQrcode;
